import { Injectable } from '@angular/core';
import { Container, Graphics, Sprite } from 'pixi.js';
import { _screenX, _screenY } from '../../../../services/canvas/canvas.service';
import {
  Coords,
  Gloves,
  GradientBgOptions,
  SceneBackgroundDefinition,
  VideoBgOptions,
} from '../../../../elements/resource/types/shape.type';
import { FileRootShape } from '../../../shape/shapes/general/root/file-root-shape';
import gsap from 'gsap';
import {
  CanvasBackground,
  CanvasBackgroundConfig,
} from '../../../shape/canvas-background';
import {
  EllipseBackground,
  EllipseBackgroundOptions,
  CustomEllipseBackgroundOptions,
} from '../../../shape/shapes/background/ellipse-background';
import { CustomEllipseBackground } from '../../../shape/shapes/background/custom-ellipse-background';
import { RectangleElement } from '../../../shape/shapes/primitive/rectangle-element';
import { VideoBackground } from '../../../shape/shapes/background/video-background';
import { ImageBackground } from '../../../shape/shapes/background/image-background';
import { ImageBgOptions } from '../../../../elements/resource/types/shape.type';
import { Store } from '@ngrx/store';
import { GloveBackground } from '../../../shape/shapes/background/glove-background';
import { GradientBackground } from '../../../shape/shapes/background/gradient-background';
import { cloneDeep, isEqual } from 'lodash';
import { Organisation } from '../../../../organisation/organisation.interface';
import {
  saveScene,
  selectCurrentProject,
  selectCurrentStudioScene,
  StudioProject,
  StudioScene,
  StudioSceneBackgroundDefinition,
} from '..';
import { currentOrganisation } from '../../../../organisation/organisation.selector';
import { StudioSceneShape } from '../../../shape/shapes/general/root/studio-scene-shape';
import { Subject } from 'rxjs';

export interface GloveDefinition {
  color: string;
  alpha: number;
  center?: Coords;
  radius: Coords;
  scale?: Coords;
  from?: Coords;
  after?: Coords;
  hide?: boolean;
}

export enum BackgroundType {
  Gloves = 'gloves',
  Gradient = 'gradient',
  Video = 'video',
  Image = 'image',
}

export enum GloveBackgroundType {
  Footer = 'footer',
  Header = 'header',
  Symmetric = 'symmetric',
}
export type GloveBackgroundDef = Record<string, GloveDefinition>;
export interface GradientBackgroundDef {
  colors: string[];
  type: 'linear' | 'radial';
  start: Coords;
  end?: Coords;
  r?: number;
}
export type BackgroundDefiniton = GloveBackgroundDef | GradientBackgroundDef;
export type BackgroundDef = {
  bg: {
    color: string;
    gradient?: {
      type: 'linear' | 'radial';
      start: Coords;
      end?: Coords;
      r?: number;
    };
  };
  gloves: Record<
    string,
    {
      color: string;
      alpha: number;
      center?: Coords;
      radius: Coords;
    }
  >;
};

@Injectable()
export class BackgroundService {
  prevContainer: Container;
  container: Container;
  nextContainer: Container;
  thumbnailContainer: Container;
  instance: CanvasBackground;
  thumbnailInstance: CanvasBackground;
  prevScreenRectangle: RectangleElement;
  screenRectangle: RectangleElement;
  nextScreenRectangle: RectangleElement;
  thumbnailScreenRectangle: RectangleElement;
  currentBackground: SceneBackgroundDefinition;
  instances: Record<string, CanvasBackground> = {};
  nextInstances: Record<string, CanvasBackground> = {};
  thumbnailInstances: Record<string, CanvasBackground> = {};
  currentBackgroundDefinitionId: string;
  currentOrganisation: Organisation;
  currentProject: StudioProject;
  currentScene: StudioScene;
  get colors() {
    const palette =
      this.currentOrganisation?.colorPalette ||
      this.currentProject?.colorPalette ||
      {};

    return Object.values(palette);
  }

  constructor(private readonly store: Store) {
    // -- // -- // -- //

    this.store.select(currentOrganisation).subscribe(org => {
      if (isEqual(this.currentOrganisation, org)) {
        return;
      }
      this.currentOrganisation = org;
      // console.log('currentOrganisation', this.currentOrganisation);
    });
    this.store.select(selectCurrentProject).subscribe(project => {
      this.currentProject = project;
    });

    this.store.select(selectCurrentStudioScene).subscribe(scene => {
      this.currentScene = cloneDeep(scene);
    });

    this.container = new Container();
    this.nextContainer = new Container();

    // this.nextScreenRectangle = new RectangleElement(undefined, this.container, {
    //   width: _screenX,
    //   height: _screenY,
    //   fill: '#ffffff',
    //   r: 24,
    // });

    this.container.position.y = 0;
    this.nextContainer.position.y = (9 / 8) * _screenY;

    this.thumbnailContainer = new Container();
    // This is important
    // this.store
    //   .select(selectCurrentBackgroundDefinitionId)
    //   .subscribe((id: string) => {
    //     this.currentBackgroundDefinitionId = id;
    //     console.log('selectCurrentBackgroundDefinitionId', id); //
    //     if (!id) {
    //       this.container.removeChildren();
    //       return;
    //     }
    //     this.setById(id);
    //   });
  }

  saveScene() {
    this.store.dispatch(
      saveScene({
        value: cloneDeep(this.currentScene),
      }),
    );
  }

  getPreviews(): StudioSceneBackgroundDefinition[] {
    const [c1, c2] = this.colors;
    return [
      {
        type: BackgroundType.Gloves,
        gloveType: GloveBackgroundType.Footer,
        gloves: {
          g1: {
            color: c1 || '#9fcfef',
            alpha: 0.5,
            center: [0.2, 1.1] as [number, number],
            radius: [1, 1] as [number, number],
          },
          g2: {
            color: c2 || '#9fcfef',
            alpha: 0.5,
            center: [0.8, 1.2] as [number, number],
            radius: [1, 1] as [number, number],
          },
        } as Gloves,
      },
      {
        type: BackgroundType.Gloves,
        gloveType: GloveBackgroundType.Header,
        gloves: {
          g1: {
            color: c1 || '#9fcfef',
            alpha: 0.5,
            center: [0.2, -0.1] as [number, number],
            radius: [1, 1] as [number, number],
          },
          g2: {
            color: c2 || '#9fcfef',
            alpha: 0.5,
            center: [0.8, -0.15] as [number, number],
            radius: [1, 1] as [number, number],
          },
        } as Gloves,
      },
      {
        type: BackgroundType.Gradient,
        gradient: {
          direction: 'diagonal',
          colors: this.colors.filter((_, i) => i != 0),
          type: 'linear',
          start: [0, 0],
          end: [1, 1],
        } as GradientBgOptions,
      },
    ];
  }

  initById(id: string, isPortrait = false) {
    // console.log('initById', id, 'def', this.bgs[id]);
    // if (!this.instances[id]) {
    //   this.container.removeChildren();
    //   return;
    // }
    // this.instances[id]?.addToCanvas();
    if (!id || !this.bgs?.[id]) {
      // -- //
      return this.initRandomColors(isPortrait);
    }
    const instance = this.getInstance(this.bgs[id], { isPortrait });
    return instance.container;
  }

  initStudioSceneBackground(
    def: StudioSceneBackgroundDefinition,
    isPortrait = false,
  ) {
    if (!def) {
      return this.initRandomColors(isPortrait);
    }
    const instance = this._getInstance(def, { isPortrait });
    return instance.container;
  }

  initRandomColors(isPortrait = false) {
    // const intance = this.getInstance(
    //   {
    //     type: 'gradient',
    //     options: {
    //       colors: this.colors,
    //       type: 'linear',
    //       start: [0, 0],
    //       end: [1, 1],
    //     } as GradientBgOptions,
    //   },
    //   { isPortrait },
    // );
    // console.log('initRandomColors', isPortrait);
    const intance = this.getInstance(
      {
        type: 'gloves',
        options: {
          g1: {
            color: '#9fcfef',
            alpha: 0.5,
            center: [0, 0] as [number, number],
            radius: [1, 1] as [number, number],
          },
          g2: {
            color: '#9fcfef',
            alpha: 0.5,
            center: [1, 1] as [number, number],
            radius: [1, 1] as [number, number],
          },
        } as Gloves,
      },
      { isPortrait },
    );

    return intance.container;
  }

  setById(id: string, play = false) {
    if (!this.instances[id]) {
      this.container.removeChildren();
      return;
    }

    if (this.currentBackgroundDefinitionId == id) {
      return;
    }

    this.instances[id]?.addToCanvas();
    this.setThumbnailById(id);

    if (play && this.instances[id] instanceof VideoBackground) {
      (this.instances[id] as VideoBackground).play();
    }
  }

  setThumbnailById(id: string) {
    // console.log('setThumbnailById', id);
    this.thumbnailInstances[id]?.addToCanvas();
  }

  bgs: Record<string, SceneBackgroundDefinition>;

  bgColor: string;

  initProject(
    bgs: Record<string, SceneBackgroundDefinition>,
    backgroundColor = '#ffffff',
  ) {
    if (isEqual(bgs, this.bgs)) {
      return;
    }

    this.instances = {};

    this.bgs = bgs;
    // Object.values(this.instances).forEach(instance => ins
    // console.log('------') // -- // -- // -- // -- // -- //

    // -- // -- // -- // -- //
    this.container.removeChildren();

    // new RectangleElement(undefined, this.container, {
    //   width: _screenX,
    //   height: _screenY,
    //   fill: backgroundColor,
    // });
    this.bgColor = backgroundColor;

    this.screenRectangle = new RectangleElement(undefined, this.container, {
      width: _screenX,
      height: _screenY,
      fill: backgroundColor,
      r: 24,
    });

    // new RectangleElement(undefined, this.thumbnailContainer, {
    //   width: _screenX,
    //   height: _screenY,
    //   fill: backgroundColor,
    // });

    this.thumbnailScreenRectangle = new RectangleElement(
      undefined,
      this.thumbnailContainer,
      {
        width: _screenX,
        height: _screenY,
        fill: backgroundColor,
      },
    );

    // -- // -- // -- // -- //
    Object.entries(bgs).forEach(([id, def]) => {
      this.instances[id] = this.getInstance(def);
      this.nextInstances[id] = this.getInstance(def, { next: true });
      this.thumbnailInstances[id] = this.getInstance(def, { thumbnail: true });
      // -- // -- // -- //
    });
  }

  __gsapAnimate(
    id: string,
    duration: number,
    defs: Record<string, GloveDefinition>,
    ease: string,
  ) {
    // -- // -- //
    return this.instances[id]?.gsapAnimate(duration, defs, ease);
  }

  initNext(rootShape: FileRootShape) {
    const bgId = rootShape.descriptor.backgroundDefinitionId;
    this.nextInstances[bgId]?.addToCanvas();
  }

  reset() {
    Object.values(this.instances).forEach(instance => instance?.reset());
  }

  _getInstance(
    def: StudioSceneBackgroundDefinition,
    config: CanvasBackgroundConfig = {},
  ) {
    switch (def.type) {
      case BackgroundType.Gloves:
        return new GloveBackground(this, def.gloves, config);
      case BackgroundType.Gradient:
        return new GradientBackground(this, def.gradient, config);
      case BackgroundType.Video:
        return new VideoBackground(this, def.video, config);
      case BackgroundType.Image:
        return new ImageBackground(this, def.image, config);
    }
  }

  getInstance(
    def: SceneBackgroundDefinition,
    config: CanvasBackgroundConfig = {},
  ) {
    // -- // -- // -- //
    switch (def.type) {
      case 'ellipse':
        // -- // -- //
        return new EllipseBackground(
          this,
          def.options as EllipseBackgroundOptions,
          config,
        );
      case 'custom-ellipse':
        return new CustomEllipseBackground(
          this,
          def.options as CustomEllipseBackgroundOptions,
          config,
        );
      case 'gloves':
        return new GloveBackground(this, def.options as Gloves, config);
      case 'video':
        return new VideoBackground(this, def.options as VideoBgOptions, config);
      case 'image':
        return new ImageBackground(this, def.options as ImageBgOptions, config);
      case 'gradient':
        return new GradientBackground(
          this,
          def.options as GradientBgOptions,
          config,
        );
    }
  }

  // setBackgroundByTime(time: number) {
  //   this.instance?.setStateByTime(time);
  // }
  _setBackgroundByTime(id: string, time: number) {
    console.log('_setBackgroundByTime', id, time);
    this.instances[id]?.setStateByTime(time);
  }

  _setThumbnailBackgroundByTime(id: string, time: number) {
    console.log('_setThumbnailBackgroundByTime', id, time);
    // console.log('instance', this.thumbnailInstances[id]);
    this.thumbnailInstances[id]?.setStateByTime(time);
  }

  hideGloves(duration: number) {
    return (this.instances['2'] as GloveBackground).hideGloves(duration);
  }

  ellipseRatio: number;
  getEllipseCoords(center: Coords, radius: number, angle: number): Coords {
    return [
      center[0] + radius * Math.cos(angle),
      center[1] + radius * Math.sin(angle),
    ];
  }
  touch(): void {}
  randomDef() {
    return this.generateRandomDef('#0a1f3a', '#3c96d2ff', '#8b5cf6');
  }

  gsapAnimate(start: number, end: number) {
    return this.instance?._animate(start, end);
  }

  _gsAnimate(id: string, start: number, end?: number) {
    return this.instances[id]?._animate(start, end);
  }

  _regenerateBackground() {
    // -- // -- // -- //
    return {
      ...this.currentBackground,
      colors: [this.randomHexColor(), this.randomHexColor()],
    };
  }

  getNextGloveType(
    currentType: GloveBackgroundType | undefined,
  ): GloveBackgroundType {
    switch (currentType) {
      case GloveBackgroundType.Footer:
        return GloveBackgroundType.Header;
      case GloveBackgroundType.Header:
        return GloveBackgroundType.Symmetric;
      case GloveBackgroundType.Symmetric:
        return GloveBackgroundType.Footer;
      default:
        return GloveBackgroundType.Footer;
    }
  }

  getGlovePositions(gloveType: GloveBackgroundType): {
    g1Center: Coords;
    g2Center: Coords;
  } {
    switch (gloveType) {
      case GloveBackgroundType.Header:
        return {
          g1Center: [0.2, -0.1] as Coords,
          g2Center: [0.8, -0.15] as Coords,
        };
      case GloveBackgroundType.Symmetric:
        return {
          g1Center: [0.1, 0.1] as Coords,
          g2Center: [0.9, 0.9] as Coords,
        };
      case GloveBackgroundType.Footer:
      default:
        return {
          g1Center: [0.2, 1.1] as Coords,
          g2Center: [0.8, 1.2] as Coords,
        };
    }
  }

  regenerateBackground(sceneShape: StudioSceneShape, isPortrait = false) {
    // const [c1, c2] = this.colors;
    // console.log('colors', this.colors);
    const colors = this.colors;

    const randomColorIndex = this.randInt(0, colors.length - 1);
    const c1 = colors[randomColorIndex];
    const c2 = colors[(randomColorIndex + 1) % colors.length];

    const currentGloveType = this.currentScene?.background?.gloveType;
    const nextGloveType = this.getNextGloveType(currentGloveType);
    const { g1Center, g2Center } = this.getGlovePositions(nextGloveType);

    const gloves = {
      g1: {
        color: c1 || '#9fcfef',
        alpha: 0.5,
        center: g1Center,
        radius: [1, 1] as [number, number],
      },
      g2: {
        color: c2 || '#9fcfef',
        alpha: 0.5,
        center: g2Center,
        radius: [1, 1] as [number, number],
      },
    } as Gloves;

    this.currentScene.background = {
      type: BackgroundType.Gloves,
      gloveType: nextGloveType,
      gloves,
    };
    this.saveScene();
  }

  randomHexColor() {
    return (
      '#' +
      Math.floor(Math.random() * 16777215)
        .toString(16)
        .padStart(6, '0')
    );
  }

  animate(current: FileRootShape, next: FileRootShape): gsap.core.Timeline {
    const tl = gsap.timeline();
    // const currentGloves = current.background?.gloves;
    // const nextDef = next.background?.def;
    // if (!nextDef) return tl;
    // const nextGloves = nextDef.gloves || {};
    // const nextKeys = Object.keys(nextGloves);
    // const count = Math.min(currentGloves.length, nextKeys.length);
    // for (let i = 0; i < count; i++) {
    //   const sprite = currentGloves[i];
    //   const target = nextGloves[nextKeys[i]];
    //   const [tx, ty] = target.center || [_screenX / 2, _screenY / 2];
    //   console.log('animation.glove.timeline', tx, ty);
    //   if (sprite) {
    //     tl.to(sprite, { x: tx, y: ty, duration: 2, ease: 'power2.out' }, 0);
    //   }
    // }
    return tl;
  }

  // -- //

  glovesByIRI: Record<string, Array<Graphics | Sprite>> = {};

  // bgRectangle: Graphics;
  sprite: Sprite;

  /**
   * Generates a random BackgroundDef from three input colors.
   * - bg.color will use the first color
   * - bg.gradient is randomly linear or radial unless forced via opts
   * - gloves are 2..4 blurred ellipses with random sizes/positions/colors
   */
  generateRandomDef(
    c1: string,
    c2: string,
    c3: string,
    opts?: {
      glowCount?: [number, number];
      gradient?: 'linear' | 'radial' | 'any';
    },
  ): BackgroundDef {
    const colors = [c1, c2, c3];

    // Decide gradient type
    const gradientType: 'linear' | 'radial' =
      opts?.gradient && opts.gradient !== 'any' ? opts.gradient : 'linear';
    // : Math.random() < 0.6
    //   ? 'radial'
    //   : 'linear';

    // Build gradient config
    let gradient: BackgroundDef['bg']['gradient'];
    if (gradientType === 'radial') {
      const start: Coords = [
        this.rand(_screenX * 0.35, _screenX * 0.65),
        this.rand(_screenY * 0.35, _screenY * 0.65),
      ];
      const r = this.rand(
        Math.max(_screenX, _screenY) / 3,
        Math.max(_screenX, _screenY) / 1.2,
      );
      gradient = { type: 'radial', start, r };
    } else {
      // simple horizontal/vertical/random diagonal
      const mode = this.randInt(0, 2);
      let start: Coords;
      let end: Coords;
      if (mode === 0) {
        // horizontal left -> right
        start = [0, this.rand(0, _screenY)];
        end = [_screenX, start[1]];
      } else if (mode === 1) {
        // vertical top -> bottom
        start = [this.rand(0, _screenX), 0];
        end = [start[0], _screenY];
      } else {
        // diagonal
        start = [this.rand(0, _screenX * 0.3), this.rand(0, _screenY * 0.3)];
        end = [
          this.rand(_screenX * 0.7, _screenX),
          this.rand(_screenY * 0.7, _screenY),
        ];
      }
      gradient = { type: 'linear', start, end };
    }

    // Determine number of glows
    const [gmin, gmax] = opts?.glowCount || [2, 4];
    const glowCount = this.randInt(gmin, gmax);

    const gloves: BackgroundDef['gloves'] = {};
    for (let i = 0; i < glowCount; i++) {
      const key = `g${i + 1}`;
      const color = this.pick(colors);
      const alpha = this.rand(0.18, 0.45);
      const center: Coords = [
        this.rand(_screenX * 0.1, _screenX * 0.9),
        this.rand(_screenY * 0.1, _screenY * 0.9),
      ];
      const radius: Coords = [
        this.rand(_screenX * 0.12, _screenX * 0.28),
        this.rand(_screenY * 0.1, _screenY * 0.26),
      ];
      gloves[key] = { color, alpha, center, radius };
    }

    return {
      // bg: { color: '#000000', gradient },
      bg: { color: c1, gradient },
      gloves,
    };
  }

  private rand(min: number, max: number): number {
    return Math.random() * (max - min) + min;
  }

  private randInt(min: number, max: number): number {
    return Math.floor(this.rand(min, max + 1));
  }

  private pick<T>(arr: T[]): T {
    return arr[Math.floor(Math.random() * arr.length)];
  }

  _createDefinition() {
    const typeOptions = ['linear', 'radial', 'base'];

    // -- // -- //
    // switch (typeOptions) {
    //   case 'linear':
    //     break;
    //   case 'radial':
    //     break;
    // }
  }

  /**
   * Returns a gently transformed BackgroundDef to create smooth animation across 2-3 steps.
   * Keeps colors and element structure; only adjusts coordinates and radii slightly.
   * Feed the returned value back into this method for the next frame.
   */
  transformForAnimation(
    def: BackgroundDef,
    opts?: { amount?: number },
  ): BackgroundDef {
    const amt = Math.max(0.001, Math.min(opts?.amount ?? 0.22, 0.5));
    const midX = _screenX / 2;
    const midY = _screenY / 2;

    // Transform gradient definition
    const g = def.bg.gradient;
    const gradient = g
      ? g.type === 'radial'
        ? {
            type: 'radial' as const,
            start: this.nudgePoint(g.start, midX, midY, amt),
            r: this.nudgeRadius(
              g.r ?? Math.max(_screenX, _screenY) / 2,
              amt,
              0.6,
              1.4,
            ),
          }
        : {
            type: 'linear' as const,
            start: this.nudgePoint(g.start, midX, midY, amt),
            end: g.end ? this.nudgePoint(g.end, midX, midY, amt) : undefined,
          }
      : undefined;

    // Transform glove centers and radii
    const gloves: BackgroundDef['gloves'] = {};
    Object.keys(def.gloves || {}).forEach(key => {
      const it = def.gloves[key];
      const center = this.nudgePoint(
        it.center || [midX, midY],
        midX,
        midY,
        amt,
      );
      const radius = this.nudgeRadiusPair(it.radius, amt);
      gloves[key] = { color: it.color, alpha: it.alpha, center, radius };
    });

    return {
      bg: { color: def.bg.color, gradient },
      gloves,
    };
  }

  private nudgePoint(
    [x, y]: Coords,
    midX: number,
    midY: number,
    amt: number,
  ): Coords {
    // Rotate slightly around center, then apply stronger drift + swirl
    // const dx = x - midX,
    //   dy = y - midY;
    // const theta = amt * 1.2;
    // const cosT = Math.cos(theta),
    //   sinT = Math.sin(theta);
    // const rx = dx * cosT - dy * sinT;
    // const ry = dx * sinT + dy * cosT;
    // const xr = rx + midX;
    // const yr = ry + midY;
    // const towardX = (midX - xr) * amt * 0.8;
    // const towardY = (midY - yr) * amt * 0.8;
    // const swirlX = Math.sin((xr + yr) * 0.002) * amt * 100;
    // const swirlY = Math.cos((xr - yr) * 0.002) * amt * 100;
    // const nx = this.clamp(xr + towardX + swirlX, 0, _screenX);
    // const ny = this.clamp(yr + towardY + swirlY, 0, _screenY);
    return [x, y].map(
      val => val + (120 + Math.random() * 100) * (Math.random() < 0.5 ? -1 : 1),
    ) as Coords;
  }

  private nudgeRadius(
    r: number,
    amt: number,
    minScale = 0.96,
    maxScale = 1.04,
  ): number {
    const scale =
      minScale + (maxScale - minScale) * (0.5 + 0.5 * Math.sin(r * 0.005));
    const rr = r * (1 + (scale - 1) * amt);
    const maxR = Math.max(_screenX, _screenY);
    return this.clamp(rr, 10, maxR);
  }

  private nudgeRadiusPair([rx, ry]: Coords, amt: number): Coords {
    const nrx = this.nudgeRadius(rx, amt, 0.8, 1.25);
    const nry = this.nudgeRadius(ry, amt, 0.8, 1.25);
    return [nrx, nry];
  }

  private clamp(v: number, min: number, max: number): number {
    return Math.min(max, Math.max(min, v));
  }
}
